import { useRef, useState } from 'react';

import { useCommuneSearch } from '@/hooks/useCommuneSearch.ts';
import type { Commune } from '@shared/types/entities';
import { ChevronDown, Loader2, MapPin, X } from 'lucide-react';

interface CityComboboxProps {
  value: string;
  onChange: (value: string) => void;
  onSelect: (commune: Commune) => void;
  department?: string;
  placeholder?: string;
  className?: string;
}

export function CityCombobox({
  value,
  onChange,
  onSelect,
  department,
  placeholder = 'Rechercher une commune…',
  className = '',
}: CityComboboxProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { communes, isLoading, search, clear } = useCommuneSearch({ department });

  const handleInput = (q: string) => {
    onChange(q);
    search(q);
    setOpen(true);
  };

  const handleSelect = (commune: Commune) => {
    onChange(commune.name);
    onSelect(commune);
    clear();
    setOpen(false);
  };

  const handleClear = () => {
    onChange('');
    clear();
    setOpen(false);
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div className='relative'>
        <MapPin className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none' />
        <input
          type='text'
          value={value}
          onChange={e => handleInput(e.target.value)}
          onFocus={() => communes.length > 0 && setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder={placeholder}
          className='w-full pl-10 pr-16 py-2.5 border border-slate-200 rounded-xl bg-slate-50 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm transition-colors'
          autoComplete='off'
        />
        <div className='absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1'>
          {isLoading ? (
            <Loader2 className='w-4 h-4 text-slate-400 animate-spin' />
          ) : value ? (
            <button type='button' onClick={handleClear} className='text-slate-400 hover:text-slate-600'>
              <X className='w-4 h-4' />
            </button>
          ) : (
            <ChevronDown className='w-4 h-4 text-slate-400' />
          )}
        </div>
      </div>

      {open && communes.length > 0 && (
        <ul className='absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden max-h-56 overflow-y-auto'>
          {communes.map(commune => (
            <li key={commune.citycode}>
              <button
                type='button'
                onMouseDown={() => handleSelect(commune)}
                className='w-full flex items-center justify-between gap-2 px-3 py-2.5 hover:bg-slate-50 text-left transition-colors'
              >
                <div className='flex items-center gap-2'>
                  <MapPin className='w-3.5 h-3.5 text-slate-400 shrink-0' />
                  <span className='text-sm text-slate-700'>{commune.name}</span>
                </div>
                <span className='text-xs text-slate-400 shrink-0'>
                  {commune.postcode[0] ?? ''} · {commune.departmentName ?? commune.department}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
